import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const CATEGORIES = [
  { key: 'general', label: 'General', icon: '⚙️', description: 'Platform name, email, and tenant limits' },
  { key: 'security', label: 'Security', icon: '🔒', description: 'Login attempts, lockout, passwords, sessions' },
  { key: 'subscription', label: 'Subscription', icon: '💳', description: 'Auto-suspend and reminder scheduling' },
  { key: 'email', label: 'Email / SMTP', icon: '📧', description: 'SMTP server configuration for outgoing emails' },
  { key: 'backup', label: 'Backup', icon: '💾', description: 'Automatic backup schedule and settings' },
];

// Validation rules per setting key
const VALIDATION = {
  max_tenants:             { min: 1, max: 10000, label: '1 – 10,000' },
  default_max_users:       { min: 1, max: 1000, label: '1 – 1,000' },
  max_login_attempts:      { min: 1, max: 20, label: '1 – 20' },
  lockout_duration_minutes:{ min: 1, max: 1440, label: '1 – 1,440 min' },
  password_min_length:     { min: 4, max: 32, label: '4 – 32' },
  session_timeout_minutes: { min: 5, max: 1440, label: '5 – 1,440 min' },
  smtp_port:               { min: 1, max: 65535, label: '1 – 65,535' },
};

// Settings that contain sensitive data
const SENSITIVE_KEYS = ['smtp_password'];

export default function PlatformSettings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('general');
  const [edited, setEdited] = useState({});
  const [toast, setToast] = useState(null); // { type: 'success'|'error', message }
  const [showSensitive, setShowSensitive] = useState({});

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/settings');
      setSettings(res.data.data || []);
    } catch { setToast({ type: 'error', message: 'Failed to load settings' }); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Auto-dismiss toast after 4s
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const categorySettings = settings.filter(s => s.category === activeCategory);
  const hasChanges = Object.keys(edited).length > 0;

  const handleChange = (key, value) => {
    setEdited(prev => {
      const orig = settings.find(s => s.setting_key === key);
      if (orig && orig.setting_value === value) {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      }
      return { ...prev, [key]: value };
    });
  };

  const validate = () => {
    for (const [key, val] of Object.entries(edited)) {
      const rule = VALIDATION[key];
      if (rule) {
        const num = parseInt(val, 10);
        if (isNaN(num) || num < rule.min || num > rule.max) {
          return `${key.replace(/_/g, ' ')}: must be between ${rule.min} and ${rule.max}`;
        }
      }
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { setToast({ type: 'error', message: err }); return; }

    setSaving(true);
    try {
      const res = await api.put('/settings', { settings: Object.entries(edited).map(([k, v]) => ({ key: k, value: v })) });
      setToast({ type: 'success', message: res.data.message || 'Settings saved and applied immediately' });
      setEdited({});
      fetchSettings();
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.message || 'Failed to save settings' });
    } finally { setSaving(false); }
  };

  const discard = () => { setEdited({}); setToast({ type: 'info', message: 'Changes discarded' }); };

  const getValue = (s) => edited.hasOwnProperty(s.setting_key) ? edited[s.setting_key] : s.setting_value;

  const renderInput = (s) => {
    const val = getValue(s);
    const isChanged = edited.hasOwnProperty(s.setting_key);
    const base = `w-full px-3 py-2 border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 ${isChanged ? 'border-blue-400 bg-blue-50/30 ring-1 ring-blue-200' : 'border-gray-200'}`;

    // Boolean-like settings (toggle switch)
    if (['true', 'false'].includes(s.setting_value) || s.setting_key.includes('enabled') || s.setting_key.includes('auto_') || s.setting_key.includes('secure')) {
      return (
        <div className="flex items-center space-x-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={val === 'true' || val === true} onChange={e => handleChange(s.setting_key, String(e.target.checked))} className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
          </label>
          <span className={`text-sm ${(val === 'true' || val === true) ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
            {(val === 'true' || val === true) ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      );
    }

    // Sensitive fields (password input)
    if (SENSITIVE_KEYS.includes(s.setting_key)) {
      const visible = showSensitive[s.setting_key];
      return (
        <div className="relative">
          <input type={visible ? 'text' : 'password'} value={val} onChange={e => handleChange(s.setting_key, e.target.value)} className={base + ' pr-10'} placeholder="••••••••" />
          <button type="button" onClick={() => setShowSensitive(p => ({ ...p, [s.setting_key]: !p[s.setting_key] }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs">
            {visible ? '🙈' : '👁️'}
          </button>
        </div>
      );
    }

    // Number-like settings
    if (/^\d+$/.test(s.setting_value)) {
      const rule = VALIDATION[s.setting_key];
      return (
        <div>
          <input type="number" value={val} onChange={e => handleChange(s.setting_key, e.target.value)}
            min={rule?.min} max={rule?.max} className={base} />
          {rule && <div className="text-xs text-gray-400 mt-1">Range: {rule.label}</div>}
        </div>
      );
    }

    // Select for backup_schedule
    if (s.setting_key === 'backup_schedule') {
      return (
        <select value={val} onChange={e => handleChange(s.setting_key, e.target.value)} className={base}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      );
    }

    // Multi-line settings (long values)
    if (s.setting_value && s.setting_value.length > 80) {
      return <textarea rows={3} value={val} onChange={e => handleChange(s.setting_key, e.target.value)} className={base} />;
    }

    // Default text
    return <input type="text" value={val} onChange={e => handleChange(s.setting_key, e.target.value)} className={base} placeholder={s.setting_key === 'smtp_host' ? 'smtp.gmail.com' : ''} />;
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center space-x-2 transition-all animate-slide-in ${
          toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          toast.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          <span>{toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : 'ℹ️'}</span>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="ml-2 text-current opacity-50 hover:opacity-100">✕</button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Platform Settings</h2>
          <p className="text-sm text-gray-500 mt-0.5">Changes apply immediately after saving — affects login, tenant creation, sessions, and more</p>
        </div>
        {hasChanges && (
          <div className="flex items-center space-x-3">
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full font-medium">{Object.keys(edited).length} unsaved change(s)</span>
            <button onClick={discard} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">Discard</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        )}
      </div>

      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-56 flex-shrink-0 space-y-1">
          {CATEGORIES.map(c => {
            const catCount = settings.filter(s => s.category === c.key).length;
            const catEdited = Object.keys(edited).filter(k => settings.find(s => s.setting_key === k && s.category === c.key)).length;
            return (
              <button key={c.key} onClick={() => setActiveCategory(c.key)}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center space-x-2 transition-colors ${activeCategory === c.key ? 'bg-blue-50 text-blue-700 font-medium shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
                <span>{c.icon}</span>
                <div className="flex-1">
                  <span>{c.label}</span>
                  <span className="ml-1 text-xs text-gray-400">({catCount})</span>
                </div>
                {catEdited > 0 && <span className="w-2 h-2 rounded-full bg-amber-400" />}
              </button>
            );
          })}
        </div>

        {/* Settings Form */}
        <div className="flex-1 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
            <h3 className="font-semibold text-gray-900">{CATEGORIES.find(c => c.key === activeCategory)?.label} Settings</h3>
            <p className="text-xs text-gray-400 mt-0.5">{CATEGORIES.find(c => c.key === activeCategory)?.description}</p>
          </div>
          {categorySettings.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">No settings in this category yet</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {categorySettings.map(s => (
                <div key={s.setting_key} className={`px-6 py-4 grid grid-cols-3 gap-4 items-start transition-colors ${edited.hasOwnProperty(s.setting_key) ? 'bg-blue-50/20' : ''}`}>
                  <div className="col-span-1">
                    <div className="text-sm font-medium text-gray-900">{s.setting_key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
                    {s.description && <div className="text-xs text-gray-400 mt-0.5">{s.description}</div>}
                    <div className="text-[10px] text-gray-300 mt-1 font-mono">{s.setting_key}</div>
                  </div>
                  <div className="col-span-2">{renderInput(s)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
